// StepPersonalDetails.js
import React, { useEffect, useState, useRef, useCallback } from "react";
import DMQCustomCard from "../../components/common/DMQCustomCard";
import SystemFetchDetails from "../../components/common/SystemFetchDetails";

import {
  saveStepData,
  getStoredFormData,
  saveCurrentStep,
  getCurrentInternalStep,
  clearCurrentInternalStep,
  saveCurrentInternalStep,
  getEditingMode,
  setEditingMode,
  getQuestionBank,
  setUserNRIDetails,
  getUserNRIDetails,
  saveDMQMedicalConditions,
  getDMQMedicalConditions,
} from "../../utils/formStorage";

import FormFooter from "../../components/common/FormFooter";
import { useLoader } from "../../context/LoaderContext";

import { DMQStepNames, MAIN_STEPS } from "../../constants/constants";

import { checkUserInactivity } from "../../utils/checkUserInactivity";
import { getAuthMemberData } from "../../utils/authStorage";

import DynamicFormRenderer_Helix from "../../components/dynamicRendering/DyamicFormRenderer_Helix";
import { withAuth } from "../../utils/withAuth";
// import { Button } from "@digitallabs/one-x-ui";
import { Button } from "../../resourses/digitallabs/one-x-ui";
import { adaptAddressSchema } from "../../utils/AddressSchemaAdapter";
import { showMessage } from "../../utils/toastMessageUtil";

const QUESTIONNAIRE_CONFIG = [
  {
    key: "isUserNRI",
    stepId: MAIN_STEPS.NRI_QUESTIONNAIRE,
    title: "NRI Questionnaire",
    condition: (personalDetails) => personalDetails.nationality === "Indian" && personalDetails.residentialStatus === "NRI",
  },
  {
    key: "isUserFN",
    stepId: MAIN_STEPS.FOREIGN_NATIONAL_QUESTIONNAIRE,
    title: "Foreign National Questionnaire",
    condition: (personalDetails) => personalDetails.nationality === "Non-Indian/OCI/PIO",
  },
];

const StepPersonalDetails = ({ handleStepChange }) => {
  const { setLoading } = useLoader();
  const formRef = useRef(null);

  const [formState, setFormState] = useState({});
  const [renderingJSON, setRenderingJSON] = useState([]);

  const [systemDetails, setSystemDetails] = useState(null);
  const [systemExpanded, setSystemExpanded] = useState(false);

  const [internalStep, setInternalStep] = useState(getCurrentInternalStep() || 1);

  const [isFormValid, setIsFormValid] = useState(false);

  const totalInternalSteps = 2;
  const [questionBank, setQuestionBankState] = useState(null);

  const storedConditions = getDMQMedicalConditions();

  const [conditions, setConditions] = useState({
    isUserNRI: storedConditions?.isUserNRI || false,
    isUserFN: storedConditions?.isUserFN || false,
  });

  useEffect(() => {
    const qb = getQuestionBank();
    if (qb) {
      setQuestionBankState(qb);
    }
  }, []);

  /*
  FORM STATE UPDATE
  */
  const handleChange = useCallback((name, value) => {
    setFormState((prev) => {
      if (Object.is(prev[name], value)) return prev;
      return {
        ...prev,
        [name]: value,
      };
    });
  }, []);

  // QUESTION BANK RENDER

  const renderQuestionBank = (step) => {
    if (!questionBank) return;

    if (step === 1) {
      setRenderingJSON(questionBank[DMQStepNames.PERSONAL_DETAILS] || []);
    } else if (step === 2) {
      const isUserNRI = getUserNRIDetails();
      const adaptedSchema = adaptAddressSchema(questionBank[DMQStepNames.ADDRESS_DETAILS], isUserNRI);
      setRenderingJSON(adaptedSchema || []);
    }
  };

  /*
  FETCH SYSTEM DETAILS
  */
  useEffect(() => {
    const fetchUser = async () => {
      const user = await getAuthMemberData();
      setSystemDetails(user);
    };

    fetchUser();
  }, []);

  /*
  HEADER BACK SYNC
  */
  useEffect(() => {
    const syncInternalStep = (event) => {
      if (event.detail?.internalStep) {
        setInternalStep(event.detail.internalStep);
      }
    };

    window.addEventListener("dmq-step-change", syncInternalStep);

    return () => {
      window.removeEventListener("dmq-step-change", syncInternalStep);
    };
  }, []);

  /*
  STEP LOAD
  */

  useEffect(() => {
    if (!questionBank) return;

    setLoading(true);

    saveCurrentStep(MAIN_STEPS.PERSONAL_DETAILS);
    renderQuestionBank(internalStep);

    const stored = getStoredFormData() || {};

    if (internalStep === 1 && stored[DMQStepNames.PERSONAL_DETAILS]) {
      setFormState(stored[DMQStepNames.PERSONAL_DETAILS]);
    } else if (internalStep === 2 && stored[DMQStepNames.ADDRESS_DETAILS]) {
      setFormState(stored[DMQStepNames.ADDRESS_DETAILS]);
    } else {
      setFormState({});
    }

    setLoading(false);
  }, [internalStep, questionBank]); //  ADD THIS

  /*
  SCROLL TOP
  */
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [internalStep]);

  /*
  SUBMIT LOGIC
  */
  const onFinish = (values) => {
    checkUserInactivity(null, async () => {
      setLoading(true);

      const stepConfig = {
        1: {
          name: DMQStepNames.PERSONAL_DETAILS,
          internalStep: 1,
          processValues: (vals) => ({
            ...vals,
            ...systemDetails,
          }),
          next: 2,
        },

        2: {
          name: DMQStepNames.ADDRESS_DETAILS,
          internalStep: 2,
          processValues: (vals) => vals,
          next: null,
        },
      };

      const config = stepConfig[internalStep];

      if (!config) {
        setLoading(false);
        return;
      }

      const finalValues = config.processValues(values);
      saveStepData(config.name, finalValues);

      const previousIsUserNRI = conditions.isUserNRI;
      const previousIsUserFN = conditions.isUserFN;

      const currentIsUserNRI = QUESTIONNAIRE_CONFIG[0].condition(finalValues);
      const currentIsUserFN = QUESTIONNAIRE_CONFIG[1].condition(finalValues);

      if (config.name === DMQStepNames.PERSONAL_DETAILS) {
        const personalDetails = finalValues || {};

        //  Build conditions dynamically
        const computedConditions = {};

        QUESTIONNAIRE_CONFIG.forEach((q) => {
          computedConditions[q.key] = q.condition(personalDetails, {
            getUserNRIDetails,
          });
        });

        setConditions(computedConditions);
        saveDMQMedicalConditions(computedConditions);
      }

      const isEditing = getEditingMode();

      const isNRISame = currentIsUserNRI === previousIsUserNRI;
      const isFNSame = currentIsUserFN === previousIsUserFN;

      /*
      EDIT MODE FLOW
      */
      if (isEditing) {
        if (config.internalStep === 1) {
          if (isNRISame && isFNSame) {
            setUserNRIDetails(currentIsUserNRI);
            handleStepChange(MAIN_STEPS.CONFIRM_DETAILS);
            clearCurrentInternalStep();
            setEditingMode(false);
          } else {
            showMessage("warning", "Since you updated Nationality/Residential Status please re-enter Address Details.");

            setUserNRIDetails(currentIsUserNRI);
            saveStepData(DMQStepNames.ADDRESS_DETAILS, {});
            setInternalStep(config.next);
            saveCurrentInternalStep(config.next);

            setEditingMode(true);
          }
        } else {
          handleStepChange(MAIN_STEPS.CONFIRM_DETAILS);

          clearCurrentInternalStep();
          setEditingMode(false);
        }
      } else {
        /*
        NORMAL FLOW
        */

        if (config.next) {
          if (!isNRISame || !isFNSame) {
            saveStepData(DMQStepNames.ADDRESS_DETAILS, {});
            showMessage("warning", "Since you updated Nationality/Residential Status please re-enter Address Details.");
          }

          setInternalStep(config.next);
          saveCurrentInternalStep(config.next);
          renderQuestionBank(config.next);
        } else {
          handleStepChange(MAIN_STEPS.MEDICAL_HISTORY);
          clearCurrentInternalStep();
          setEditingMode(false);
        }
      }

      setFormState({});
      setLoading(false);
    });
  };

  const handleSystemToggle = () => {
    setSystemExpanded(!systemExpanded);
  };

  const onValidationChange = useCallback((isValid) => {
    setIsFormValid((prev) => (prev === isValid ? prev : isValid));
  }, []);

  return (
    <div style={{ paddingBottom: "80px" }}>
      <SystemFetchDetails expanded={systemExpanded} systemDetails={systemDetails} handleToggle={handleSystemToggle} />

      <br />

      <DMQCustomCard
        title={internalStep === 1 ? "Personal Details" : "Address Details"}
        subtitle={internalStep === 1 ? "Please provide the remaining details to continue." : "Please provide your residential and work addresses."}
        totalDots={totalInternalSteps}
        activeDot={internalStep}
      >
        <DynamicFormRenderer_Helix
          ref={formRef}
          step={MAIN_STEPS.PERSONAL_DETAILS}
          internalStep={internalStep}
          schema={renderingJSON}
          formState={formState}
          handleChange={handleChange}
          onFormFinish={() => onFinish(formState)}
          onFormValidityChange={onValidationChange}
        />
      </DMQCustomCard>

      <FormFooter>
        <Button
          variant="primary"
          size="md"
          fullWidth
          className={!isFormValid ? "next-btn-disabled" : ""}
          onClick={() => formRef.current?.triggerNext()}
        >
          {getEditingMode() ? "Confirm" : "Next"}
        </Button>
      </FormFooter>
    </div>
  );
};

export default withAuth(StepPersonalDetails);
